package com.sddamico.draftmachine.pages.draft

import com.sddamico.draftmachine.data.DraftOrder
import com.sddamico.draftmachine.data.PickOrigin
import com.sddamico.draftmachine.models.PickResponse
import kotlin.random.Random

/** A team entered in the wizard, tracked client-side by a stable [localId] until it's persisted. */
data class WizardTeam(
  val localId: String,
  val teamName: String,
  val ownerName: String,
  val ownerEmail: String,
  val source: TeamSource = TeamSource.Manual,
  val externalTeamId: String? = null,
)

/**
 * Where a wizard team came from. [Fantrax] rows arrive with a name but no owner — Fantrax carries
 * no owner name or email — so the roster step renders them with inline owner fields to fill in.
 */
enum class TeamSource { Manual, Fantrax, Espn }

/**
 * One pick in the wizard's working order, referencing teams by their client-side [localId]. The
 * flat order list is always kept round-major (all round-1 slots, then round-2, …) so a slot's
 * overall position is simply its index + 1.
 *
 * @param originTeamLocalId the pick's *original* owner, preserved across reassignment (TRADED).
 * @param origin marks a reassigned (TRADED) or inserted (COMPENSATORY) pick; null for a plain pick.
 * @param id a stable identity for this slot (survives reordering and reassignment) so the editor
 *   can `key()` its rows; freshly assigned whenever a slot is constructed without one.
 */
data class OrderSlot(
  val teamLocalId: String,
  val round: Int,
  val originTeamLocalId: String? = null,
  val origin: PickOrigin? = null,
  val skipped: Boolean = false,
  val id: Int = nextSlotId(),
)

/**
 * Rebuild the working order from picks the server already persisted — the lottery path, where the
 * draw commits the pick sequence before the LM ever sees it. Picks reference teams by their real
 * id, so [localIdOf] (teamId → localId) translates them back into the wizard's own id space; the
 * resume flow's identity map makes it a no-op there, but a wizard run from step 1 still holds
 * client-side localIds and would otherwise resolve no team at all. [teamCount] backs the round
 * fallback for picks with no round stored (mirrors `DraftRepository.roundOf`).
 */
fun orderSlotsFrom(
  picks: List<PickResponse>,
  teamCount: Int,
  localIdOf: Map<String, String> = emptyMap(),
): List<OrderSlot> =
  picks.sortedBy { it.position }.map { pick ->
    OrderSlot(
      teamLocalId = localIdOf[pick.teamId] ?: pick.teamId,
      round = pick.round ?: if (teamCount > 0) ((pick.position - 1) / teamCount) + 1 else 1,
      originTeamLocalId = pick.originTeamId?.let { localIdOf[it] ?: it },
      origin = pick.origin,
      skipped = pick.skipped,
    )
  }

/**
 * Whether two orders describe the same pick sequence. [OrderSlot.id] is a client-only counter that
 * changes on every reseed, so comparing data classes directly would report a freshly reloaded order
 * as different from the identical one it replaced.
 */
fun sameOrder(a: List<OrderSlot>, b: List<OrderSlot>): Boolean =
  a.size == b.size && a.indices.all { i ->
    val (x, y) = a[i] to b[i]
    x.teamLocalId == y.teamLocalId && x.round == y.round &&
      x.originTeamLocalId == y.originTeamLocalId && x.origin == y.origin && x.skipped == y.skipped
  }

/** Toggle whether a custom-order slot should be automatically skipped when its turn arrives. */
fun toggleSkipped(order: List<OrderSlot>, index: Int): List<OrderSlot> {
  if (index !in order.indices) return order
  return order.toMutableList().also { it[index] = order[index].copy(skipped = !order[index].skipped) }
}

/** Mark this slot and every later slot currently held by the same team as skipped. */
fun skipTeamFrom(order: List<OrderSlot>, index: Int): List<OrderSlot> {
  if (index !in order.indices) return order
  val teamLocalId = order[index].teamLocalId
  return order.mapIndexed { slotIndex, slot ->
    if (slotIndex >= index && slot.teamLocalId == teamLocalId) slot.copy(skipped = true) else slot
  }
}

private var slotIdCounter = 0
private fun nextSlotId(): Int = slotIdCounter++

/**
 * Generate the pick order for a format from the entered team order. LINEAR repeats it every round;
 * SNAKE reverses it on even rounds. CUSTOM is hand-built in the editor (seeded from
 * [seedCustomOrder]); LOTTERY is disabled and yields nothing.
 */
fun generateOrder(order: DraftOrder, teams: List<WizardTeam>, rounds: Int): List<OrderSlot> = when (order) {
  DraftOrder.LINEAR, DraftOrder.SNAKE -> expandFirstRound(order, seedFirstRound(teams), rounds)
  DraftOrder.CUSTOM -> seedCustomOrder(teams, rounds)
  DraftOrder.LOTTERY -> emptyList()
}

/** The starting point for the custom editor: the entered team order, repeated each round (linear). */
fun seedCustomOrder(teams: List<WizardTeam>, rounds: Int): List<OrderSlot> =
  generateOrder(DraftOrder.LINEAR, teams, rounds)

/** Round 1 in the entered team order. */
fun seedFirstRound(teams: List<WizardTeam>): List<OrderSlot> = teams.map { OrderSlot(it.localId, 1) }

/** Round 1 in a random team order. */
fun randomFirstRound(teams: List<WizardTeam>, random: Random = Random.Default): List<OrderSlot> =
  seedFirstRound(teams.shuffled(random))

/**
 * The team sequence a SNAKE or LINEAR draft repeats after round 1: each regular round-1 slot's
 * original owner. A round-1 trade or skip affects only that pick, and a compensatory pick is a
 * one-off, so none of them carry into later rounds.
 */
fun baseOrderOf(firstRound: List<OrderSlot>): List<String> =
  firstRound.filter { it.origin != PickOrigin.COMPENSATORY }.map { it.originTeamLocalId ?: it.teamLocalId }

/**
 * Build the full SNAKE or LINEAR order from an edited round 1: round 1 exactly as given, then
 * [baseOrderOf] it repeated (LINEAR) or alternately reversed (SNAKE) for the remaining rounds.
 */
fun expandFirstRound(order: DraftOrder, firstRound: List<OrderSlot>, rounds: Int): List<OrderSlot> {
  require(order == DraftOrder.SNAKE || order == DraftOrder.LINEAR) { "Only SNAKE and LINEAR expand a first round" }
  if (rounds < 1) return emptyList()
  val base = baseOrderOf(firstRound)
  val later = (2..rounds).flatMap { r ->
    val seq = if (order == DraftOrder.SNAKE && r % 2 == 0) base.reversed() else base
    seq.map { OrderSlot(it, r) }
  }
  return firstRound + later
}

/**
 * Whether [firstRound] still describes [teams]: every team holds exactly one regular slot and no
 * slot references a team that has since been removed. A roster edit after round 1 was set breaks
 * this, and the round has to be reseeded.
 */
fun firstRoundMatches(firstRound: List<OrderSlot>, teams: List<WizardTeam>): Boolean {
  val ids = teams.map { it.localId }.toSet()
  val base = baseOrderOf(firstRound)
  return base.size == ids.size && base.toSet() == ids && firstRound.all { it.teamLocalId in ids }
}

/** The 0-based indices of [order] whose slots belong to [round], in order. */
fun indicesInRound(order: List<OrderSlot>, round: Int): List<Int> =
  order.indices.filter { order[it].round == round }

/** Swap the slot at [index] with the previous slot when both are in the same round; else unchanged. */
fun moveUp(order: List<OrderSlot>, index: Int): List<OrderSlot> {
  if (index <= 0 || index >= order.size) return order
  if (order[index - 1].round != order[index].round) return order
  return order.toMutableList().also { it[index] = order[index - 1]; it[index - 1] = order[index] }
}

/** Swap the slot at [index] with the next slot when both are in the same round; else unchanged. */
fun moveDown(order: List<OrderSlot>, index: Int): List<OrderSlot> = moveUp(order, index + 1)

/**
 * Insert a COMPENSATORY pick for [teamLocalId] at the end of [round]'s block, keeping the list
 * round-major so overall positions stay contiguous.
 */
fun insertCompensatory(order: List<OrderSlot>, round: Int, teamLocalId: String): List<OrderSlot> {
  val roundIndices = indicesInRound(order, round)
  val insertAt = if (roundIndices.isEmpty()) order.size else roundIndices.last() + 1
  val slot = OrderSlot(teamLocalId, round, origin = PickOrigin.COMPENSATORY)
  return order.toMutableList().also { it.add(insertAt, slot) }
}

/**
 * Reassign the pick at [index] to [newTeamLocalId], stamping it TRADED and preserving the pick's
 * original owner (the first owner is kept across repeated reassignment).
 */
fun reassign(order: List<OrderSlot>, index: Int, newTeamLocalId: String): List<OrderSlot> {
  if (index < 0 || index >= order.size) return order
  val slot = order[index]
  if (newTeamLocalId == slot.teamLocalId && slot.origin != PickOrigin.COMPENSATORY) return order
  val original = slot.originTeamLocalId ?: slot.teamLocalId
  val updated = slot.copy(
    teamLocalId = newTeamLocalId,
    originTeamLocalId = original,
    origin = PickOrigin.TRADED,
  )
  return order.toMutableList().also { it[index] = updated }
}

/** Formats whose full order is generated from an editable round 1. */
val DraftOrder.expandsFirstRound: Boolean get() = this == DraftOrder.SNAKE || this == DraftOrder.LINEAR
